import { useEffect, useRef, useState } from "react";
import { getCicloVida } from "../../api/ciclo.api";

const SPRINTS_OPCOES = [4, 8, 12, 24];

/* Cores padrão por etapa, usadas como fallback se o backend não enviar */
const ETAPA_COR_DEFAULT = {
  /* QA */
  qa_novo:                    "#a78bfa",
  qa_aguardando_analise:      "#818cf8",
  qa_aguardando_aprovacao:    "#c084fc",
  qa_ajuste_especificacao:    "#e879f9",
  /* Produção */
  aguardando_distribuicao:    "#60a5fa",
  aguardando_desenvolvimento: "#38bdf8",
  em_curso:                   "#fbbf24",
  /* QA Testes */
  qt_resolvido:               "#4ade80",
  qt_aguardando_testes:       "#34d399",
  qt_em_teste:                "#2dd4bf",
  qt_reteste:                 "#86efac",
  qt_aguardando_versao:       "#22d3ee",
  /* Implantação */
  imp_feedback:               "#f97316",
};

/* Agrupamento de etapas por setor */
const SETORES = [
  {
    key: "qa", label: "QA Triagem", cor: "#a78bfa",
    chaves: ["qa_novo", "qa_aguardando_analise", "qa_aguardando_aprovacao", "qa_ajuste_especificacao"],
  },
  {
    key: "producao", label: "Produção", cor: "#fbbf24",
    chaves: ["aguardando_distribuicao", "aguardando_desenvolvimento", "em_curso"],
  },
  {
    key: "qa_testes", label: "QA Testes", cor: "#4ade80",
    chaves: ["qt_resolvido", "qt_aguardando_testes", "qt_em_teste", "qt_reteste", "qt_aguardando_versao"],
  },
  {
    key: "implantacao", label: "Implantação", cor: "#f97316",
    chaves: ["imp_feedback"],
  },
];

function fmt(d, dec = 1) {
  if (d == null || d === 0) return "—";
  if (d < 1) return `${Math.round(d * 24)}h`;
  return `${Number(d).toFixed(dec)}d`;
}

function barPct(val, max) {
  if (!val || !max) return 0;
  return Math.min(100, Math.round(val / max * 100));
}

/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
export default function CicloVida() {
  const [data,      setData]      = useState(null);
  const [sprints,   setSprints]   = useState(8);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("todos");
  const loadRef = useRef(null);

  async function load(qtd = sprints) {
    setLoading(true);
    setError(false);
    try {
      const res = await getCicloVida(qtd);
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = () => load(sprints);

  useEffect(() => { load(sprints); }, [sprints]); // eslint-disable-line

  const etapas = data?.etapas || [];

  /* Etapas do tipo selecionado */
  const etapasFiltradas = tipoFiltro !== "todos" && data?.por_tipo?.[tipoFiltro]
    ? etapas.map(e => {
        const tipoEtapa = data.por_tipo[tipoFiltro].find(t => t.chave === e.chave);
        return tipoEtapa ? { ...e, ...tipoEtapa } : e;
      })
    : etapas;

  const tipos = data?.por_tipo ? ["todos", ...Object.keys(data.por_tipo)] : ["todos"];

  const totalMedio = tipoFiltro !== "todos" && data?.por_tipo?.[tipoFiltro]
    ? data.por_tipo[tipoFiltro].reduce((s, e) => s + (e.media || 0), 0)
    : data?.total_medio;

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", textAlign: "center", paddingTop: 60, fontSize: 13 }}>
        Calculando ciclo de vida dos tickets…
      </div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", paddingTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={() => loadRef.current()} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Ciclo de Vida do Ticket</h1>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
            Tempo médio por etapa do fluxo · {data?.periodo
              ? `${data.periodo.sprints} sprints · ${data.periodo.de} → ${data.periodo.ate}`
              : `últimas ${sprints} sprints`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Filtro por tipo */}
          <select
            value={tipoFiltro}
            onChange={e => setTipoFiltro(e.target.value)}
            style={selectStyle}
          >
            {tipos.map(t => (
              <option key={t} value={t}>{t === "todos" ? "Todos os tipos" : t}</option>
            ))}
          </select>
          {/* Período */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Período de análise
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {SPRINTS_OPCOES.map(n => (
                <button
                  key={n}
                  onClick={() => setSprints(n)}
                  style={{
                    padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                    fontSize: 11, fontWeight: 500, transition: "all 0.15s",
                    background: sprints === n ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.04)",
                    border: sprints === n ? "1px solid rgba(96,165,250,0.4)" : "1px solid rgba(255,255,255,0.1)",
                    color: sprints === n ? "#60a5fa" : "rgba(255,255,255,0.4)",
                  }}
                >
                  {n} sprints
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => loadRef.current()} style={btnRefresh}>↻</button>
        </div>
      </header>

      {etapas.length === 0 ? (
        <div style={{ color: "#334155", textAlign: "center", paddingTop: 40, fontSize: 13 }}>
          Nenhum dado disponível para o período selecionado.
        </div>
      ) : (
        <>
          {/* ── KPI por setor ── */}
          <div style={kpiRow}>
            <div style={{ ...kpiCard, borderColor: "rgba(96,165,250,0.2)" }}>
              <div style={kpiLabel}>Tempo Total Médio</div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#60a5fa", lineHeight: 1 }}>
                {fmt(totalMedio, 1)}
              </div>
              <div style={{ fontSize: 11, color: "#334155", marginTop: 4 }}>da criação até o fechamento</div>
            </div>
            {SETORES.map(setor => {
              const total = setor.chaves.reduce((sum, chave) => {
                const e = etapasFiltradas.find(x => x.chave === chave);
                return sum + (e?.media || 0);
              }, 0);
              return (
                <div key={setor.key} style={{ ...kpiCard, borderColor: `${setor.cor}30` }}>
                  <div style={{ ...kpiLabel, color: setor.cor }}>{setor.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: setor.cor, lineHeight: 1 }}>
                    {fmt(total)}
                  </div>
                  <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>total do setor</div>
                </div>
              );
            })}
          </div>

          {/* ── Visão por Setor ── */}
          {(() => {
            const maxSetorMedia = Math.max(...SETORES.map(s =>
              s.chaves.reduce((sum, chave) => {
                const e = etapasFiltradas.find(x => x.chave === chave);
                return sum + (e?.media || 0);
              }, 0)
            ), 0.01);

            return (
              <div style={{ display: "flex", gap: 10, alignItems: "stretch", marginBottom: 16 }}>
                {SETORES.map((setor, si) => {
                  const etapasSetor = setor.chaves
                    .map(chave => etapasFiltradas.find(e => e.chave === chave))
                    .filter(Boolean);
                  const totalSetor = etapasSetor.reduce((s, e) => s + (e.media || 0), 0);
                  const maxEtapaSetor = Math.max(...etapasSetor.map(e => e.media || 0), 0.01);
                  const isGargaloSetor = totalSetor === maxSetorMedia;
                  const gargaloChave = etapasFiltradas.reduce((a, b) => (a.media || 0) > (b.media || 0) ? a : b, {}).chave;

                  return (
                    <div key={setor.key} style={{ display: "flex", alignItems: "stretch", flex: setor.chaves.length }}>
                      {/* Setor card */}
                      <div style={{
                        flex: 1,
                        background: "rgba(11,22,42,0.72)",
                        backdropFilter: "blur(18px)",
                        border: `1px solid ${setor.cor}30`,
                        borderTop: `3px solid ${setor.cor}`,
                        borderRadius: 14,
                        padding: "16px 18px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}>
                        {/* Cabeçalho do setor */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: setor.cor, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                              {setor.label}
                            </span>
                            {isGargaloSetor && (
                              <span style={{
                                fontSize: 9, fontWeight: 800, color: "#f87171",
                                background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.3)",
                                padding: "1px 6px", borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase",
                              }}>gargalo</span>
                            )}
                          </div>
                          <span style={{ fontSize: 18, fontWeight: 900, color: setor.cor }}>
                            {fmt(totalSetor)}
                          </span>
                        </div>

                        {/* Barra proporcional do setor */}
                        <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${barPct(totalSetor, maxSetorMedia)}%`, background: setor.cor, borderRadius: 2 }} />
                        </div>

                        {/* Etapas do setor */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {etapasSetor.map((e, ei) => {
                            const cor = e.cor || ETAPA_COR_DEFAULT[e.chave] || setor.cor;
                            const isGargaloEtapa = e.chave === gargaloChave;
                            return (
                              <div key={e.chave}>
                                {/* Separador entre etapas */}
                                {ei > 0 && (
                                  <div style={{ borderTop: `1px dashed ${setor.cor}18`, marginBottom: 6 }} />
                                )}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: cor, flexShrink: 0 }} />
                                    <span style={{ fontSize: 11, color: "#94a3b8" }}>{e.label}</span>
                                    {isGargaloEtapa && (
                                      <span style={{
                                        fontSize: 8, fontWeight: 800, color: "#f87171",
                                        background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)",
                                        padding: "1px 5px", borderRadius: 3, letterSpacing: "0.05em", textTransform: "uppercase",
                                      }}>gargalo</span>
                                    )}
                                  </div>
                                  <span style={{ fontSize: 14, fontWeight: 800, color: cor }}>{fmt(e.media)}</span>
                                </div>
                                {/* Barra da etapa relativa ao max da etapa no setor */}
                                <div style={{ height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                                  <div style={{ height: "100%", width: `${barPct(e.media, maxEtapaSetor)}%`, background: cor, borderRadius: 2 }} />
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#475569" }}>
                                  <span>med {fmt(e.mediana)}</span>
                                  <span>mín {fmt(e.min)} · máx {fmt(e.max)}</span>
                                  <span>{e.tickets ?? "—"} tk</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Seta entre setores */}
                      {si < SETORES.length - 1 && (
                        <div style={{ display: "flex", alignItems: "center", padding: "0 6px", color: "#334155", fontSize: 18, flexShrink: 0 }}>→</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── Comparativo por tipo ── */}
          {data?.por_tipo && Object.keys(data.por_tipo).length > 1 && (
            <div style={glass}>
              <div style={sectionLabel}>Comparativo por Tipo de Ticket</div>
              <div style={{ overflowX: "auto" }}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>Tipo</th>
                      {etapas.map(e => (
                        <th key={e.chave} style={{ ...th, textAlign: "right", color: e.cor || ETAPA_COR_DEFAULT[e.chave] || "#60a5fa" }}>
                          {e.label}
                        </th>
                      ))}
                      <th style={{ ...th, textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.por_tipo).map(([tipo, tipoEtapas]) => {
                      const totalTipo = tipoEtapas.reduce((s, e) => s + (e.media || 0), 0);
                      return (
                        <tr key={tipo} style={trRow}
                          onMouseEnter={ev => { ev.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                          onMouseLeave={ev => { ev.currentTarget.style.background = "transparent"; }}
                        >
                          <td style={{ ...td, fontWeight: 600, color: "#e2e8f0" }}>{tipo}</td>
                          {etapas.map(e => {
                            const te = tipoEtapas.find(t => t.chave === e.chave);
                            const cor = e.cor || ETAPA_COR_DEFAULT[e.chave] || "#60a5fa";
                            return (
                              <td key={e.chave} style={{ ...td, textAlign: "right", color: cor, fontWeight: 600 }}>
                                {fmt(te?.media)}
                              </td>
                            );
                          })}
                          <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "#60a5fa" }}>
                            {fmt(totalTipo)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Styles ── */

const page = { padding: "28px 32px", minHeight: "100vh", color: "#f1f5f9" };

const header = {
  display: "flex", alignItems: "flex-start",
  justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap",
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const selectStyle = {
  background: "rgba(11,22,42,0.8)", border: "1px solid rgba(96,165,250,0.15)",
  borderRadius: 6, color: "#94a3b8", fontSize: 12, padding: "5px 10px", cursor: "pointer", outline: "none",
};

const kpiRow = {
  display: "grid",
  gridTemplateColumns: `1.2fr repeat(4, 1fr)`,
  gap: 10,
  marginBottom: 16,
  overflowX: "auto",
};

const kpiCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  border: "1px solid",
  borderRadius: 12,
  padding: "14px 16px",
  minWidth: 100,
};

const kpiLabel = {
  fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
  marginBottom: 16,
};

const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14,
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };

const th = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  color: "#475569", borderBottom: "1px solid rgba(96,165,250,0.08)",
  whiteSpace: "nowrap", letterSpacing: "0.05em", textTransform: "uppercase",
};

const td = {
  padding: "10px 14px",
  borderBottom: "1px solid rgba(255,255,255,0.04)",
  verticalAlign: "middle",
};

const trRow = { transition: "background 0.12s" };
